import { yupResolver } from '@hookform/resolvers/yup'
import { ControlledTextField } from '@if/mui'
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import AsyncActionButton from '@src/shared/components/AsyncActionButton'
import SelectComponent from '@src/shared/components/SelectComponent'
import { useCreateNewTicket } from '@src/tools/error-tracker/api/exceptions-api'
import { reportErrorToConsole } from '@src/shared/services/error-logger'
import { NewTicketRequestDto } from '@src/tools/error-tracker/types/BulkActions'
import { ExceptionLog } from '@src/tools/error-tracker/types/ExceptionLog'
import { SelectValueListItem } from '@src/shared/types/SelectValueListItem'
import { translateError } from '@src/shared/utils/translateError'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { newTicketSchema, type NewTicketFormData } from './NewTicketDialog-schema'

interface NewTicketDialogProps {
  selectedIds: string[]
  selectedExceptions: ExceptionLog[]
  severityValues: SelectValueListItem[]
  plateformeValues: SelectValueListItem[]
  open: boolean
  onSuccess: (ticketNumber: string) => void
  onCancel: () => void
}

const NewTicketDialog = ({
  selectedIds,
  selectedExceptions,
  severityValues,
  plateformeValues,
  open,
  onSuccess,
  onCancel,
}: NewTicketDialogProps) => {
  const { t } = useTranslation()
  const [createNewTicket, isCreating, createError, resetCreate] = useCreateNewTicket()

  // Generate default title based on selected exceptions
  const generateDefaultTitle = (exceptions: ExceptionLog[]): string => {
    if (exceptions.length === 0) return 'Fix Application Issues'

    const applicationNames = [...new Set(exceptions.map((e) => e.applicationName))]

    if (applicationNames.length === 1) {
      return `Fix ${applicationNames[0]}`
    } else {
      return 'Fix Multiple Applications'
    }
  }

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewTicketFormData>({
    resolver: yupResolver(newTicketSchema),
    defaultValues: {
      ...newTicketSchema.getDefault(),
      title: generateDefaultTitle(selectedExceptions),
    },
  })

  useEffect(() => {
    reset({
      ...newTicketSchema.getDefault(),
      title: generateDefaultTitle(selectedExceptions),
    })
    resetCreate()
  }, [open, reset, resetCreate, selectedExceptions])

  const onSubmit = async (formData: NewTicketFormData): Promise<void> => {
    try {
      const request: NewTicketRequestDto = {
        exceptionLogIds: selectedIds,
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        severity: formData.severity,
        plateforme: formData.plateforme,
      }
      const updatedExceptions = await createNewTicket(request)
      const ticketNumber = updatedExceptions[0]?.devopsTicketNumber ?? ''
      onSuccess(ticketNumber)
    } catch (err) {
      reportErrorToConsole(err)
    }
  }

  const handleCancel = () => {
    reset()
    resetCreate()
    onCancel()
  }

  const selectedCount = selectedIds.length

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{t('dialogs.newTicket.title', 'Create New Ticket')}</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('dialogs.newTicket.error', 'An error occurred while creating the ticket. Please try again.')}
            </Alert>
          )}

          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('dialogs.newTicket.confirmMessage', {
              defaultValue: 'Create a new ticket for {{count}} selected {{items}}?',
              count: selectedCount,
              items: selectedCount === 1 ? 'item' : 'items',
            })}
          </Typography>

          <ControlledTextField
            name="title"
            control={control}
            label={t('dialogs.newTicket.titleLabel', 'Title *')}
            placeholder={t('dialogs.newTicket.titlePlaceholder', 'Enter ticket title')}
            fullWidth
            disabled={isCreating}
            helperText={translateError(t, errors.title)}
            sx={{ mb: 2 }}
          />

          <ControlledTextField
            name="description"
            control={control}
            label={t('dialogs.newTicket.descriptionLabel', 'Description (Optional)')}
            placeholder={t('dialogs.newTicket.descriptionPlaceholder', 'Add additional details about the issue...')}
            multiline
            rows={4}
            fullWidth
            disabled={isCreating}
            helperText={translateError(t, errors.description)}
            sx={{ mb: 2 }}
          />

          <Stack direction="row" spacing={2}>
            <SelectComponent
              {...register('severity')}
              label={t('dialogs.newTicket.severityLabel', 'Severity *')}
              valueList={severityValues}
              error={errors.severity}
              disabled={isCreating}
            />
            <SelectComponent
              {...register('plateforme')}
              label={t('dialogs.newTicket.plateformeLabel', 'Plateforme *')}
              valueList={plateformeValues}
              error={errors.plateforme}
              disabled={isCreating}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <AsyncActionButton variant="outlined" onClick={handleCancel} disabled={isCreating} type="button">
            {t('common.cancel', 'Cancel')}
          </AsyncActionButton>
          <AsyncActionButton
            variant="contained"
            color="primary"
            isPending={isCreating}
            disabled={isCreating || selectedCount === 0}
            type="submit"
          >
            {t('dialogs.newTicket.createButton', 'Create Ticket')}
          </AsyncActionButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default NewTicketDialog
